import { useCallback, useEffect, useRef, useState } from "react";
import { useHomeContextUser } from "../../homeContext.ts";
import useUpdatingRef from "../../../../common/core-ui/useUpdatingRef.ts";
import useUserPreference from "../../actions/user/useUserPreference.ts";
import { UserPreferenceKey } from "../../../../common/api/decimal/methods/user.ts";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import { logEvent } from "../../../../common/api/monitoring/index.ts";
import { FacultyWelcomeModalPreference } from "./facultyWelcomeModalPreference.ts";
import useSampleSearchEligibility from "../../search/layout/useSampleSearchEligibility.ts";

export type FacultyWelcomeModalCohort = "verified" | "unverified";
export type FacultyWelcomeModalDismissMethod =
  | "close"
  | "primary_cta"
  | "secondary_cta";

const IMPRESSION_CAP = 3;
const RESHOW_DAYS = 30;

export default function useFacultyWelcomeModal() {
  const user = useHomeContextUser();
  const isFeatureEnabled =
    useBrandLoaderData().InstructorHome.WelcomeModal.enabled;
  const { isSampleSearchEnabled } = useSampleSearchEligibility();
  const [preference, updatePreference, _deletePreference, updatedDate] =
    useUserPreference<FacultyWelcomeModalPreference>(
      UserPreferenceKey.FacultyWelcomeModalState
    );

  const impressions = preference?.impressions ?? 0;
  const permanentlySuppressed = preference?.permanentlySuppressed ?? false;
  const daysSinceLastSeen = updatedDate
    ? (Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24)
    : Infinity;
  const cohort: FacultyWelcomeModalCohort = user.instructor.verified
    ? "verified"
    : "unverified";
  const rawShouldShow =
    isFeatureEnabled &&
    isSampleSearchEnabled &&
    user.instructor.hasRole &&
    !permanentlySuppressed &&
    impressions < IMPRESSION_CAP &&
    (impressions === 0 || daysSinceLastSeen >= RESHOW_DAYS);

  const hasLoggedImpressionRef = useRef(false);
  // holds the impression number logged just below so dismiss/logCtaClick can
  // report it immediately, without waiting for updatePreference's fetcher
  // round-trip to flow the new value back through `impressions`
  const impressionNumberRef = useRef(impressions);
  // kept in sync so `dismiss` can read the latest suppression state without
  // taking a dependency that would churn its identity
  const permanentlySuppressedRef = useUpdatingRef(permanentlySuppressed);

  // once shown, stay shown for this mount even after the impression's
  // own optimistic preference update recomputes rawShouldShow to false
  const [hasShown, setHasShown] = useState(false);
  const shouldShow = rawShouldShow || hasShown;

  useEffect(() => {
    if (rawShouldShow && !hasLoggedImpressionRef.current) {
      hasLoggedImpressionRef.current = true;
      setHasShown(true);
      const impressionNumber = impressions + 1;
      impressionNumberRef.current = impressionNumber;
      updatePreference({
        permanentlySuppressed: false,
        impressions: impressionNumber,
      });
      logEvent("faculty_welcome_modal_impression", {
        cohort,
        impressionNumber,
      });
    }
  }, [rawShouldShow, cohort, impressions, updatePreference]);

  const dismiss = useCallback(
    (method: FacultyWelcomeModalDismissMethod) => {
      updatePreference({
        permanentlySuppressed: permanentlySuppressedRef.current,
        impressions: impressionNumberRef.current,
      });
      logEvent("faculty_welcome_modal_dismissed", {
        cohort,
        impressionNumber: impressionNumberRef.current,
        method,
      });
    },
    [cohort, updatePreference, permanentlySuppressedRef]
  );

  const logCtaClick = useCallback(
    (method: "primary_cta" | "secondary_cta") => {
      logEvent("faculty_welcome_modal_cta_click", {
        cohort,
        impressionNumber: impressionNumberRef.current,
        method,
      });
    },
    [cohort]
  );

  return { shouldShow, cohort, dismiss, logCtaClick };
}
